import { getDefaultEnsureCampaignDataOptions } from "app/lib/data/models/CampaignObjects";
import { getDefaultTrackingDataOptions, TrackingDataOptions, TrackingDataQueryParams } from "app/lib/types/TrackingDataOptions";
import { getAccountProviders, getAccountTimezone, getInnerError } from "app/lib/utils/adaya/AdayaUtils";
import { ProviderNames } from "lib/data/models/Providers";
import qs from 'qs';
import ApiService from "services/ApiService";
import EventService from "services/EventService";
import IDbOrCacheStore from "stores/IDbOrCacheStore";
import { hasAllProviderData, processDailyAccountDatas } from 'utils/adaya/ProviderUtils';
import { mapDaysBetweenDates } from "utils/DateUtils";
import { getLogger } from "utils/logging";
import { ensureRequest, handlePolledRequest } from "utils/RequestUtils";

const { log, warn } = getLogger('ProviderService', { color: 'green', enabled: true });

// TODO: manage provider states;

/**
 * @description This class specifically handles communication with the local provider store for daily data,
 * and our backend, to ensure the provider data is available locally.
 * @class ProviderService
 */
class ProviderService {

    // current states of providers, ie. "valid" or not usable because of some error, etc.
    private dayStore: IDbOrCacheStore;

    constructor() {
        this.dayStore = new IDbOrCacheStore('DAILY_DATA');
        EventService.subscribe(AppEvents.LOGIN, this.clearData);
        EventService.subscribe(AppEvents.LOGOUT, this.clearData);
    }

    /**
     * @description Primary job handler which ensures the provider data is available locally, or otherwise
     * fetches it from the server. It translates the resulting collection of {DailyAccountData} (by day>providers),
     * into DailyCampaignData objects per day (with all providers combined to one collection).
     * @param {*} account
     * @param {*} from
     * @param {*} to
     */
    public getDailyCampaignData = async (account, filter, opts?: EnsureCampaignDataOptions) => {
        try {
            opts = getDefaultEnsureCampaignDataOptions(opts); // ensure defaults
            const timezone = getAccountTimezone(account);
            const from = filter.timeRange?.start_date;
            const to = filter.timeRange?.end_date;
            if (!from || !to) throw "Missing from or to date.";

            let providers = getAccountProviders(account);
            if (filter.onlyProviders) providers = providers.filter(p => filter.onlyProviders.filter(_p => _p == p.name).length > 0);
            log(`getDailyCampaignData:`, account?._id, providers, filter, opts);

            // consider data empty if we want to ask the server
            let campaignData = (opts.forceUpdate || !opts.dontFetchNewData) ? undefined :
                await this.getSavedDailyAccountData(from, to, timezone, providers.map(p => p.name));

            if (opts.dontFetchNewData && hasAllProviderData(providers, campaignData, from, to, timezone, opts.forceUpdate)) {
                log('✅ All provider daily data is complete.', opts.dontFetchNewData ? '(dontFetchNewData)' : '', campaignData);
                // convert the DailyAccountData into DailyCampaignData:
                return processDailyAccountDatas(campaignData, from, to, timezone);
            } else {
                log(!opts.dontFetchNewData ?
                    `Request asks for new data. Fetching...` :
                    `Request doesn't want new data, but some provider data is incomplete. Fetching...`);
            }

            // Incomplete data, fetch for new...

            //log('Fetching incomplete provider data...', opts, providers);
            let r = await this.fetchDailyAccountData(account, filter, providers, opts);

            if (r?.success && r?.data) {
                // fetch was successfully completed, store each day locally.
                for (const dailyAccountData of r.data) {
                    await this.saveDailyAccountData(dailyAccountData, true);
                }

                // data is fetched and stored, now pull and return from store
                campaignData = await this.getSavedDailyAccountData(from, to, timezone, providers.map(p => p.name));

                log('Retrieved and saved new provider daily data.');
                return processDailyAccountDatas(campaignData, from, to, timezone);
            }

            // could not fetch, return request anyway so caller can handle
            return r;
        } catch (e) {
            let err = getInnerError(e);
            log("Exception in getDailyCampaignData: ", account._id, filter, opts, err);
            // TODO: should process each error and update ProviderStates.
            throw err;
        }
    }

    /**
     * @description Gets all the local daily data for the given dates, and converts/hydrates each to
     * a DailyCampaignData object, returning them by day: { day: DailyCampaignData }
     * @param {*} account
     * @param {*} from
     * @param {*} to
     */
    public getSavedDailyAccountData = async (from, to?, timezone?, onlyProviders?: Array<string>): Array<any> => {
        const campaignDataDays = [];
        const days = mapDaysBetweenDates(from, to, false, timezone);

        for (const d of days) {
            const data = await this.dayStore.get(d);
            if (data) {
                if (onlyProviders?.length && data.providers?.length) {
                    data.providers = data.providers.filter(p => onlyProviders.includes(p.name));
                }
                campaignDataDays.push(data);
            }
        }

        return campaignDataDays;
    }

    /**
     * @description Ask server for provider data for the given date range.
     * @param {*} account
     * @param {*} from
     * @param {*} to
     * @return {*} [DailyAccountData {
                accountId: string;
                day: string; // Format as "YYYY-MM-DD"
                providers: ProviderData[];
            }]
     */
    public fetchDailyAccountData = async (account, filter, providers?, opts?: EnsureCampaignDataOptions) => {
        try {
            log(`fetchDailyAccountData`, providers)
            return await handlePolledRequest(async (isCompleted) => {
                try {
                    let params = {
                        from: filter.timeRange.start_date, to: filter.timeRange.end_date,
                        providers: providers?.length ? providers.map(p => p.name) : undefined,
                    };
                    Object.assign(params, opts);
                    if (isCompleted) params.dontFetchNewData = true; // tell it not to fetch new data for the finished job.
                    if (filter.onlyPaidsocial) params.onlyPaidsocial = true;

                    return await ensureRequest(async () => {
                        try {
                            return await ApiService.get(`/account-data/${account._id}/campaign-data`, { params });
                        } catch (e) {
                            throw e;
                        }
                    });
                } catch (e) {
                    throw e;
                }
            });
        } catch (e) {
            console.log(`Exception in fetchDailyAccountData`, e)
            throw e;
        }
    }

    /**
     * @description Stores the set of given provider data in the daily data.
     * @param {ProviderNames} providerName
     * @param {string} from
     * @param {string} [to]
     */
    public async saveDailyAccountData(day: {}, replace = false) {
        if (!day.day) throw "No day exists in dayData.";

        log('saveDailyAccountData', day);

        // replaces each new provider day data into the existing day, if one exists locally already
        let currDay = await this.dayStore.get(day.day);
        if (currDay) {
            if (!currDay.providers) currDay.providers = [];
            // save the input providers to current day:
            day.providers.forEach(p => {
                // replace it or add it
                let cpIdx = currDay.providers.findIndex(_p => _p.name == p.name);
                if (cpIdx >= 0) {
                    currDay.providers.splice(cpIdx, 1, p);
                } else {
                    currDay.providers.push(p);
                }
            });
        } else {
            // otherwise just sets the day to the new incoming data
            currDay = day;
        }

        await this.dayStore.set(day.day, currDay);
        return currDay;
    }

    /**
     * @description Ask server for provider data for the given date range.
     * @param {*} account
     * @param {*} from
     * @param {*} to
     */
    public deleteProviderDailyData = async (account, from, to, providers?: Array<ProviderNames>, localOnly = false) => {
        console.log(`deleteProviderDailyData`, from, to, localOnly ? '(local only)' : '(all)');
        // delete from local store
        if ((!from || !to) && (!providers || !providers?.length)) {
            throw "Cannot delete entire range of provider data without specifying providers. Must supply either 'from' and 'to' parameters, or specific providers.";
        }

        const timezone = getAccountTimezone(account);

        const updateProviders = async (days) => {
            for (const d of days) {
                let dd = await this.dayStore.get(d);
                if (dd?.providers) {
                    dd.providers = dd.providers.filter(p => !providers.includes(p.name));
                    await this.dayStore.set(d, dd);
                }
            }
        }

        // delete all data for given providers
        if (!from && !to) {
            const days = await this.dayStore.getAllKeys();
            if (providers && providers.length) {
                await updateProviders(days);
            }
        }

        // delete range of data for given providers
        if (from && to) {
            const days = mapDaysBetweenDates(from, to, false, timezone);
            // delete only specific providers if given
            if (providers && providers.length) {
                await updateProviders(days);
            } else {
                // delete all the daily data for this range
                for (const d of days) {
                    await this.dayStore.remove(d);
                }
            }
        }

        if (!localOnly) {
            // delete from server
            const params = { from, to, providers }
            return await ApiService.delete(`/account-data/${account._id}/campaign-data?${qs.stringify(params)}`);
        }

        return true;
    }

    /**
     * @description Handles data by tracking parameters. For now this is not a full implementation.
     * @param {*} account
     * @param {*} from
     * @param {*} to
     */
    public getDailyTrackingData = async (account, query: TrackingDataQueryParams, opts?: TrackingDataOptions) => {
        try {
            opts = getDefaultTrackingDataOptions(opts); // ensure defaults
            const timezone = getAccountTimezone(account);
            const from = query.timeRange?.start_date;
            const to = query.timeRange?.end_date;
            if (!from || !to) throw "Missing from or to date.";

            log(`getDailyTrackingData:`, account?._id, query, opts);

            //log('Fetching incomplete provider data...', opts, providers);
            let r = await this.fetchDailyTrackingData(account, query, opts);

            if (r?.success && r?.data) {
                log('Retrieved and saved new tracking daily data.');
                return r.data;
            }

            // could not fetch, return request anyway so caller can handle
            return r;
        } catch (e) {
            let error = getInnerError(e);
            log("Exception in getDailyTrackingData: ", account._id, query, opts, error);
            // TODO: should process each error and update ProviderStates.
            throw e;
        }
    }

    /**
     * @description Ask server for tracking data grouped by a custom parameter.
     * @param {*} account
     * @param {*} from
     * @param {*} to
     * @return {*} [DailyAccountData {
                accountId: string;
                day: string; // Format as "YYYY-MM-DD"
                providers: ProviderData[];
            }]
     */
    public fetchDailyTrackingData = async (account, query: TrackingDataQueryParams, opts?: EnsureCampaignDataOptions) => {
        try {
            return await handlePolledRequest(async (isCompleted) => {
                try {
                    // todo: separate params n opts
                    let params = {
                        from: query.timeRange.start_date,
                        to: query.timeRange.end_date,
                        groupBy: query.groupBy
                    };
                    Object.assign(params, opts);
                    if (isCompleted) params.dontFetchNewData = true; // tell it not to fetch new data for the finished job.
                    if (query.onlyPaidsocial) params.onlyPaidsocial = true;

                    return await ensureRequest(async () => {
                        try {
                            return await ApiService.get(`/account-data/${account._id}/tracking-data`, { params });
                        } catch (e) {
                            throw e;
                        }
                    });
                } catch (e) {
                    throw e;
                }
            });
        } catch (e) {
            console.log(`Exception in fetchDailyTrackingData`, e)
            throw e;
        }
    }

    public clearData = async () => {
        if (this.dayStore) {
            await this.dayStore.clear();
        }
    }

}

export default new ProviderService();